import {
    GifDetails,
    fetchExtendedMetadata,
    fetchGifDetails,
    fetchLanguageTags,
    fetchMetadata,
    fetchTagCounts,
    fetchViewCount,
} from '@/api/gif'
import { Modal } from '@/app/components/modal'
import { getChannelByUsername } from '@/app/util/channel-api'
import ContentTakedown from '@/components/edit/content-takedown'
import Panel from '@/components/edit/panel-overlay'
import EditPanelSidebar from '@/components/edit/sidebar'
import { clearBulkEdit, setBulkEditOpen, useBulkEditGifs, useBulkEditOpen } from '@/components/grid/bulk-edit-state'
import { applyEditPanelAction, performEditPanelAction } from '@/util/edit/action'
import { applyEditPanelChange, performEditPanelChange } from '@/util/edit/change'
import type { IGif } from '@giphy/js-types'
import { IUser } from '@giphy/js-types'
import { EditPanelApiContext } from 'edit'
import { EditPanelExternalAction } from 'edit/panel/types/action'
import { EditPanelField } from 'edit/panel/types/field'
import { EditPanelGif } from 'edit/panel/types/gif'
import { ReactNode, SetStateAction, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { PortalMessageContext } from 'ui/src/components/message/portal-message/context'
import { create } from 'zustand'
import { useGifPermissions } from '../util/use-gif-permissions'
import UserContext from './user-context'

type EditedGifStore = { [id: string]: IGif }
const initialState: EditedGifStore = {}
const useDataStore = create<EditedGifStore>()((_) => initialState)

const setEditedGifAsync = (gif: IGif): void => {
    setTimeout(() => {
        setEditedGif(gif)
    }, 10)
}
export const setEditedGif = (gif: IGif): void => {
    useDataStore.setState((state) => ({ ...state, [gif.id]: gif }))
}
export const getEditiedGif = (gifId: IGif['id']): IGif => useDataStore.getState()[gifId]
export const useEditedGif = (gifId: IGif['id']): IGif | undefined => useDataStore((state) => state[gifId])

const isDeleted = Symbol('is_deleted')
const flagAsDeleted = (gif: EditPanelGif) => ({ ...gif, isDeleted: isDeleted }) as unknown as EditPanelGif
export const getIsDeleted = (gif?: IGif) => (gif ? (gif as unknown as any).isDeleted === isDeleted : false)

export type EditContextProps = {
    gifs: EditPanelGif[]
    addToEdit: (gifId: number | string) => void
    closeEdit: () => void
    removeFromEdit: (gifId: number | string) => void
    staffMode: boolean
    setStaffMode: React.Dispatch<SetStateAction<boolean>>
}

export const EditContext = createContext({} as EditContextProps)

const EditManager = ({ children }: { children: ReactNode }) => {
    const { user, taggerApiKey } = useContext(UserContext)
    const [staffMode, setStaffMode] = useState(false)
    const { showMessage } = useContext(PortalMessageContext)
    const state = useBulkEditGifs()
    const isBulkEdit = useBulkEditOpen()
    const rawGifs = useMemo(() => Object.values(state).map(({ gif }) => gif), [state])
    const [gifDetails, setGifDetails] = useState<GifDetails[]>([])
    // all modified gifs, doesn't clear when panels are closed
    const [loading, setLoading] = useState<boolean>(false)
    const [contentTakedown, showContentTakedown] = useState(false)
    const permissions = useGifPermissions(gifDetails)
    const gifs = useMemo(() => gifDetails.map((d) => d.gif).reverse(), [gifDetails])
    useEffect(() => {
        if (isBulkEdit) {
            const rawIds = rawGifs.map((g) => g.id).join(',')
            const detailsIds = gifDetails.map((d) => d.gif.id).join(',')
            if (rawIds !== detailsIds) {
                const getThem = async () => {
                    setLoading(true)
                    const gifDetails = await Promise.all(
                        rawGifs.map(async (gif): Promise<GifDetails> => {
                            const details = await fetchGifDetails(`${gif.id}`)
                            return details
                        })
                    )
                    setGifDetails(gifDetails)
                    setLoading(false)
                }
                getThem()
            }
        }
    }, [isBulkEdit, rawGifs, gifDetails, gifs])
    async function addToEdit(gifId: number | string) {
        if (gifDetails.findIndex((d) => `${d.gif.id}` === `${gifId}`) > -1) {
            return
        }

        try {
            setLoading(true)
            const details = await fetchGifDetails(`${gifId}`)
            setGifDetails((state) => [...state, details])
        } catch (err) {
            showMessage(`An error occurred while loading details.`, {
                style: 'error',
            })
        } finally {
            setLoading(false)
        }
    }

    function closeEdit() {
        setBulkEditOpen(false)
        clearBulkEdit()
        setGifDetails([])
    }

    function removeFromEdit(gifId: number | string) {
        setGifDetails((state) => state.filter((g) => g.gif.id !== gifId))
    }

    async function fetchUserByUsername(username: string): Promise<IUser> {
        return await getChannelByUsername({ slug: username }).then(({ user }) => user)
    }

    async function onAction<T>(gifIds: string[], action: EditPanelExternalAction, value: T) {
        await performEditPanelAction<T>(
            gifs.filter((g) => gifIds.includes(`${g.id}`)),
            action,
            value,
            user?.token,
            taggerApiKey
        )

        if (action === 'takedown') {
            showContentTakedown(true)
        }

        // apply changes that will appear in the edit panel
        setGifDetails((state) => {
            return state.map((d) => {
                if (gifIds.includes(`${d.gif.id}`)) {
                    let gif = applyEditPanelAction<T>(d.gif, action, value)
                    if (action === 'delete') {
                        gif = flagAsDeleted(gif)
                    }
                    setEditedGifAsync(gif)
                    return { ...d, gif }
                }
                return d
            })
        })
    }

    async function onChange<T>(gifIds: string[], field: EditPanelField, value: T) {
        let editGifs = gifs.filter((g) => gifIds.includes(`${g.id}`))

        // hit api
        await performEditPanelChange<T>(editGifs, field, value)

        // apply changes
        editGifs = await applyEditPanelChange<T>(editGifs, field, value)

        // update gifs
        setGifDetails((state) => {
            return state.map((d) => {
                const foundIndex = editGifs.findIndex((g) => g.id === d.gif.id)

                if (foundIndex > -1) {
                    return { ...d, gif: editGifs[foundIndex] }
                }

                return d
            })
        })
    }

    return (
        <EditContext.Provider value={{ gifs, addToEdit, closeEdit, removeFromEdit, setStaffMode, staffMode }}>
            <EditPanelApiContext.Provider
                value={{
                    fetchLanguageTags,
                    fetchMetadata,
                    fetchExtendedMetadata,
                    fetchTagCounts,
                    fetchUserByUsername,
                    fetchViewCount,
                    onAction,
                    onChange,
                }}
            >
                {children}
                {isBulkEdit ? (
                    <EditPanelSidebar permissions={permissions} gifs={gifs} onClose={closeEdit} />
                ) : (
                    <Panel gifs={gifs} loading={loading} permissions={permissions} onClose={closeEdit} />
                )}
                {contentTakedown && (
                    <Modal
                        onClose={() => {
                            showContentTakedown(false)
                        }}
                        title="Content Takedown"
                    >
                        <ContentTakedown
                            gifIds={gifs.map((g) => g.id)}
                            onClose={() => {
                                showContentTakedown(false)
                            }}
                        />
                    </Modal>
                )}
            </EditPanelApiContext.Provider>
        </EditContext.Provider>
    )
}

export default EditManager
